import React, { useState } from 'react'
import { flowRight } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Form, Field } from 'formik'
import * as Yup from 'yup'
import * as Nope from 'lib/utils/validation/Nope'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { getVehicleShareUrl } from 'lib/utils/store/vehicle'
import { getLocale } from 'lib/utils/getLocale'

import ExtendedFormik from 'components/Forms/ExtendedFormik'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import TextArea from 'components/Forms/FormElements/TextArea'
import Link from 'components/Link'
import Button from 'components/UI/Button'

import './vehicle-profile-share-form.pcss'

const VehicleProfileShareForm = ({ vehicleId, shareLink, vehicleProfileShareLogic, history }) => {
    const { t, i18n } = useTranslation(['common', 'vehicleProfile'])

    const [shareUrl] = useState(`${window.location.origin}${getLocale(i18n.language)}${getVehicleShareUrl(vehicleId, shareLink)}`)
    const [isRedirectingToPreview, setRedirectingToPreview] = useState(false)
    const [initialValues, setInitialValues] = useState({
        isActive: shareLink.isActive,
        sharePartOfPage: shareLink.sharePartOfPage,
        shareComment: shareLink.shareComment,
        shareLink: shareUrl,
    })

    return (
        <div className="vehicle-profile-share-form">
            <ExtendedFormik
                key={JSON.stringify(initialValues)}
                id="vehicle-profile-share-form"
                initialValues={initialValues}
                validationSchema={Nope.schema({
                    isActive: Yup.string(),
                    sharePartOfPage: Yup.string()
                        .required('formElements:validation.required'),
                    shareComment: Nope.message(),
                    shareLink: Yup.string(),
                })}
                onSubmit={async (values, actions) => {
                    await vehicleProfileShareLogic.updateVehicleProfileShare(shareLink.id, null, values.shareComment, values.sharePartOfPage, vehicleId)
                    actions.setSubmitting(false)
                    setInitialValues(values)

                    if (isRedirectingToPreview) {
                        history.push(`${getLocale(i18n.language)}/ajoneuvo/${vehicleId}/jaa/${shareLink.shareHash}`)
                    }
                }}
                disablePrompt
            >
                {({ values, initialValues, isSubmitting, submitForm, resetForm, dirty }) => (
                    <Form className="vehicle-profile-share-form">
                        <fieldset disabled={!values.isActive}>
                            <FormElementGroup>
                                <div className="vehicle-profile-share-form__sub-title">
                                    {t('vehicleProfile:share.subTitle')}
                                </div>
                                <Field name="shareComment">
                                    {props => (
                                        <TextArea
                                            label={t('vehicleProfile:share.shareComment')}
                                            rows="3"
                                            {...props}
                                        />
                                    )}
                                </Field>
                            </FormElementGroup>
                            <div className="vehicle-profile-share-form__actions">
                                {(values.sharePartOfPage !== initialValues.sharePartOfPage || values.shareComment !== initialValues.shareComment) && (
                                    <>
                                        <Button
                                            className="button"
                                            type="submit"
                                            trackName="submit"
                                            disabled={isSubmitting}
                                            loading={isSubmitting}
                                        >
                                            {t('common:save')}
                                        </Button>
                                        <Button
                                            className="button button--secondary"
                                            type="button"
                                            trackName="cancel"
                                            disabled={isSubmitting}
                                            loading={isSubmitting}
                                            onClick={() => resetForm()}
                                        >
                                            {t('common:cancel')}
                                        </Button>
                                    </>
                                )}
                            </div>
                            <FormElementGroup>
                                {dirty ? (
                                    <Button
                                        trackName="sharePreview"
                                        type="button"
                                        className="button button--tertiary"
                                        onClick={() => {
                                            setRedirectingToPreview(true)
                                            submitForm()
                                        }}
                                    >
                                        {t('vehicleProfile:share.previewShare')}
                                    </Button>
                                ) : (
                                    <Link
                                        trackName="sharePreview"
                                        className="button button--tertiary"
                                        to={`${getLocale(i18n.language)}/ajoneuvo/${vehicleId}/jaa/${shareLink.shareHash}`}
                                    >
                                        {t('vehicleProfile:share.previewShare')}
                                    </Link>
                                )}

                            </FormElementGroup>
                        </fieldset>
                    </Form>
                )}
            </ExtendedFormik>
        </div>
    )
}

VehicleProfileShareForm.propTypes = {
    vehicleId: PropTypes.string.isRequired,
    shareLink: PropTypes.object.isRequired,
    vehicleProfileShareLogic: PropTypes.object.isRequired,
}

export default flowRight(
    withRouter,
)(VehicleProfileShareForm)
